import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PublicShell } from "@/components/PublicShell";
import { Briefcase, Clock, MapPin, SealCheck, UsersThree } from "@phosphor-icons/react";

const Row = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
    <Icon size={16} weight="duotone" /> {children}
  </span>
);

export default function Careers() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/careers/postings").then(({ data: d }) => setData(d))
      .catch(() => setError("We could not load our openings just now. Please try again shortly."));
  }, []);

  return (
    <PublicShell testid="careers-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Careers</p>
      <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
        Work where children are known by name.
      </h1>
      {data?.intro && <p className="mt-5 text-base text-muted-foreground max-w-2xl leading-relaxed">{data.intro}</p>}

      {error && <p className="mt-8 text-sm font-semibold text-[#EF4444]" data-testid="careers-error">{error}</p>}
      {!data && !error && <p className="mt-8 text-muted-foreground">Loading open positions…</p>}

      {data && (
        <>
          <section className="mt-10" data-testid="open-positions">
            <h2 className="text-base md:text-lg font-bold">
              {data.positions.length ? `${data.positions.length} open position${data.positions.length > 1 ? "s" : ""}` : "Open positions"}
            </h2>
            {data.positions.length === 0 && (
              <div className="mt-4 bg-white rounded-2xl border border-border p-8" data-testid="no-openings">
                <p className="font-bold">We do not have a posted opening right now.</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  We only list real openings, so this page is empty when every role is filled. You are welcome to
                  join our future-opportunities pool and we will reach out when something fits.
                </p>
              </div>
            )}
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {data.positions.map((p) => (
                <article key={p.id} className="bg-white rounded-2xl border-2 border-border p-6 hover:border-[#3B82F6] hover:-translate-y-0.5"
                         style={{ transition: "transform .2s ease, border-color .2s ease" }}
                         data-testid={`position-card-${p.id}`}>
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6]">{p.role_label || p.employment_type}</span>
                  <h3 className="mt-2 font-display text-xl font-semibold">{p.title}</h3>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
                    {p.location && <Row icon={MapPin}>{p.location}</Row>}
                    <Row icon={Briefcase}>{p.employment_type}</Row>
                    {p.schedule && <Row icon={Clock}>{p.schedule}</Row>}
                  </div>
                  {p.pay_display && <p className="mt-3 font-bold">{p.pay_display}</p>}
                  {p.summary && <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{p.summary}</p>}
                  <p className="mt-4 text-xs text-muted-foreground">
                    Posted {p.posted_at ? new Date(p.posted_at).toLocaleDateString() : "recently"}
                    {p.deadline ? ` · Apply by ${p.deadline}` : ""}
                  </p>
                  <Button asChild className="mt-5 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold w-full">
                    <Link to={`/careers/${p.id}`} data-testid={`view-position-${p.id}`}>View role and apply</Link>
                  </Button>
                </article>
              ))}
            </div>
          </section>

          {data.pool_enabled && (
            <section className="mt-12 rounded-3xl bg-[#0F172A] text-white p-8 sm:p-10" data-testid="future-pool-cta">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FBBF24]">
                <UsersThree size={16} /> Future opportunities
              </span>
              <h2 className="mt-3 font-display text-2xl font-semibold">No suitable opening today?</h2>
              <p className="mt-3 text-white/80 max-w-2xl leading-relaxed text-sm">
                Tell us about yourself and we will keep your details for future openings. This is a general
                expression of interest — it is not an application for an available job, and you can ask us to stop
                contacting you at any time.
              </p>
              <Button asChild className="mt-6 rounded-full bg-white text-[#0F172A] hover:bg-[#FBBF24] font-bold">
                <Link to="/careers/apply/interest" data-testid="join-pool-btn">Join the future-opportunities pool</Link>
              </Button>
            </section>
          )}

          {data.closed_positions?.length > 0 && (
            <section className="mt-12" data-testid="closed-positions">
              <h2 className="text-base md:text-lg font-bold">Recently closed</h2>
              <div className="mt-4 space-y-3">
                {data.closed_positions.map((p) => (
                  <div key={p.id} className="bg-white rounded-2xl border border-border p-5 flex flex-wrap gap-3 justify-between items-center"
                       data-testid={`closed-position-${p.id}`}>
                    <div>
                      <p className="font-bold">{p.title}</p>
                      <p className="text-sm text-muted-foreground">{p.closed_reason}</p>
                    </div>
                    <Link to={`/careers/${p.id}`} className="text-sm font-bold text-[#3B82F6]">View details</Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="mt-12 grid gap-4 md:grid-cols-2" data-testid="careers-legal">
            <div className="bg-white rounded-2xl border border-border p-6">
              <p className="inline-flex items-center gap-2 font-bold"><SealCheck size={18} className="text-[#10B981]" weight="fill" /> Equal employment opportunity</p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed" data-testid="eeo-statement">{data.eeo_statement}</p>
            </div>
            <div className="bg-white rounded-2xl border border-border p-6">
              <p className="font-bold">Applicant privacy notice</p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="privacy-notice">{data.privacy_notice}</p>
            </div>
          </section>
        </>
      )}
    </PublicShell>
  );
}
